import {
  Asset,
  FilterData,
  FilterMetadata,
  FolderContent,
  GeometricSearchMatch,
  PartSearchMatch,
  VisualSearchMatch,
} from '@/api/generatedApi';
import { StyledListItem } from '@/components/AssetsPage/PopoverList';
import { CardGrid, getSavedCardPageCount } from '@/components/CardGrid/CardGrid';
import { SearchResultCard } from '@/components/CardGrid/SearchResultCard';
import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { DeleteAssetModal } from '@/components/modals/DeleteAssetModal';
import { BulkDeleteContentModal } from '@/components/modals/BulkDeleteContentModal';
import { CopyMenuItem, getCopyValueFromColumn } from '@/components/ContextMenu/CopyMenuItem';
import { OpenInNewTabMenuItem } from '@/components/ContextMenu/OpenInNewTabMenuItem';
import { BodyText } from '@/components/BodyText';
import { BulkActionsToolbar } from '@/components/ContentsTable/BulkActionsToolbar';
import { BulkSelectionProvider } from '@/components/ContentsTable/BulkSelectionContext';
import { DataGridLazy } from '@/components/DataGrid/DataGridLazy';
import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useCardGridData } from '@/hooks/useCardGridData';
import { useContextMenu } from '@/hooks/useContextMenu';
import { useDownloadAsset } from '@/hooks/useDownloadAsset';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useGlobalDeleteState } from '@/hooks/useGlobalDeleteState';
import { PARAM_NAMES, useSearchFilters } from '@/hooks/useSearchFilters';
import { useSearchTabs } from '@/hooks/useSearchTabs';
import { useSearchTabCounts } from '@/hooks/useSearchTabCounts';
import { ViewMode } from '@/hooks/useViewMode';
import { SearchPageObjectModel } from '@/poms/searchPageObjectModel';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { ApiErrorDisplay } from '@/types/apiError';
import { insertMetadataColumnsAfterDetails } from '@/utils/metadata';
import { DeleteOutlined as DeleteIcon, Download as DownloadIcon, FilterList, ViewColumn } from '@mui/icons-material';
import { Alert, Box, IconButton, ListItemIcon, ListItemText, Skeleton, Tooltip, useTheme } from '@mui/material';
import { SearchFilterPanel } from './SearchFilterPanel';
import {
  FooterPropsOverrides,
  GridFilterModel,
  GridLogicOperator,
  GridPreferencePanelsValue,
  GridToolbarProps,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { serializeSearchFilterModel, selectedFiltersToGridFilterModel } from './searchFilterSerializer';
import { useDebouncedFilterApply } from './useDebouncedFilterApply';
import { buildSearchContextKey } from './searchContextKey';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useScrollPositionRestore } from '@/hooks/useScrollPositionRestore';
import { useNavigationType, useSearchParams } from 'react-router-dom';
import { SearchTabs } from '../SearchTabs';
import { SearchTabCounts, SearchType } from '../types';
import { getColumns, SET_AS_SOURCE_FIELD } from './columnDefinitions';
import { SearchResultsDownload } from '../SearchResultsDownload';
import { NoSearchResults } from './NoSearchResults';

const ColumnsButton = () => {
  const apiReference = useGridApiContext();
  const theme = useTheme();

  const handleClick = () => {
    apiReference.current.showPreferences(GridPreferencePanelsValue.columns);
  };

  return (
    <Tooltip title='Columns' placement='bottom'>
      <IconButton
        size='small'
        onClick={handleClick}
        sx={{
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <ViewColumn fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const FiltersButton = () => {
  const apiReference = useGridApiContext();
  const theme = useTheme();

  const handleClick = () => {
    apiReference.current.showFilterPanel();
  };

  return (
    <Tooltip title='Filters' placement='bottom'>
      <IconButton
        size='small'
        onClick={handleClick}
        sx={{
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <FilterList fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const SearchTabsToolbar = ({
  shouldShowSearchTabs,
  toolbarActions,
  resultCounts,
  loadingStates,
}: GridToolbarProps & {
  shouldShowSearchTabs?: boolean;
  toolbarActions?: React.ReactNode;
  resultCounts?: SearchTabCounts;
  loadingStates?: Record<string, boolean>;
}) => (
  <Box
    sx={{
      padding: '8px 10px 6px 8px',
      flexWrap: 'nowrap',
      gap: '6px',
      minWidth: 0,
      overflow: 'visible',
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <Box
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        marginLeft: '8px',
      }}
    >
      {shouldShowSearchTabs && <SearchTabs resultCounts={resultCounts} loadingStates={loadingStates} />}
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
      }}
    >
      <FiltersButton />
      <ColumnsButton />
      {toolbarActions}
      <BulkActionsToolbar />
    </Box>
  </Box>
);

export const SearchTableFooter = ({
  resultCount,
  countIsLowerBound,
}: {
  resultCount?: number;
  countIsLowerBound?: boolean;
}) => {
  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        minHeight: '36px',
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px',
        flexDirection: 'row',
        gap: '6px',
      }}
    >
      <BodyText>
        Results: {resultCount === undefined ? '' : resultCount.toLocaleString()}
        {countIsLowerBound === true && '+'}
      </BodyText>
      {resultCount === undefined && <Skeleton width={30} height={20} />}
    </Box>
  );
};

const createSearchTableFooter = (totalResultCount?: number, countIsLowerBound?: boolean) => {
  const FooterComponent = (properties: FooterPropsOverrides) => (
    <SearchTableFooter resultCount={totalResultCount} countIsLowerBound={countIsLowerBound} {...properties} />
  );
  FooterComponent.displayName = 'SearchTableFooterComponent';
  return FooterComponent;
};

const useStyles = createUseStyles({
  DataGrid: {
    borderRadius: '6px !important',
    '& .MuiDataGrid-cell': {
      lineHeight: 'unset !important',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    '& .MuiDataGrid-cellCheckbox': {
      cursor: 'default',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none !important',
    },
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  dataGridWrapper: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: '24px',
  },
});

type MatchResponse = VisualSearchMatch | PartSearchMatch | GeometricSearchMatch;

export type MatchAsset = Asset & {
  contentType: 'asset';
  matchPercentage?: number;
  forwardMatchPercentage?: number;
  reverseMatchPercentage?: number;
};

export const SearchTable = ({
  fetchMatches,
  isLoading,
  error,
  shouldShowSearchTabs,
  bulkSelectionKey,
  viewMode,
  viewModeToggle,
  onSelectionCountChange,
  showFailureDiagnostics = false,
}: {
  fetchMatches: (parameters: { page: number; perPage: number }) => Promise<{
    matches: MatchResponse[];
    pageData: { total: number; totalIsLowerBound?: boolean; totalExact?: number };
    filterData?: Partial<FilterData>;
    degraded?: boolean;
  }>;
  isLoading: boolean;
  error?: ApiErrorDisplay;
  shouldShowSearchTabs: boolean;
  bulkSelectionKey?: string;
  viewMode?: ViewMode;
  viewModeToggle?: React.ReactNode;
  onSelectionCountChange?: (count: number) => void;
  /** Status page only: lets a failed row expand to the same lookup the dashboard panel uses. */
  showFailureDiagnostics?: boolean;
}) => {
  const c = useStyles();
  const { searchType, assetId, selectedFilters, searchQuery, setSelectedFilters, minThreshold, sizeThreshold } =
    useSearchFilters();
  const activeThreshold = searchType === 'visual' ? sizeThreshold : minThreshold;
  const [, setSearchParameters] = useSearchParams();
  const { asset: searchAsset } = useGetAsset({ assetId });
  const { permissions, currentUserIsAtleastTenantViewer } = useGetCurrentUser();
  const { isTaxonomyEnabled } = useFeatureFlags();
  const { isAnyDeletePending } = useGlobalDeleteState();
  const gridApi = useGridApiRef();
  const { getTenantId } = useTenants();
  const { selectedSearchTab } = useSearchTabs();
  const { counts: searchTabCounts, loadingStates: searchTabLoadingStates } = useSearchTabCounts();
  const [isDegradedSearch, setIsDegradedSearch] = useState(false);
  const [filterDataMetadata, setFilterDataMetadata] = useState<FilterMetadata | undefined>();
  const [filterDataExtensions, setFilterDataExtensions] = useState<Array<{ filter: string; count: number }>>([]);
  const [filterDataLabels, setFilterDataLabels] = useState<Array<{ filter: string; count: number }>>([]);
  const [filterDataFolders, setFilterDataFolders] = useState<Array<{ filter: string; count: number }>>([]);
  const [loadedMatchHasLabel, setLoadedMatchHasLabel] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [optimisticRemovedIds, setOptimisticRemovedIds] = useState<string[]>([]);
  const [assetToDelete, setAssetToDelete] = useState<MatchAsset | undefined>();
  const [assetsToBulkDelete, setAssetsToBulkDelete] = useState<MatchAsset[] | undefined>();

  const optimisticRemovedIdsReference = useRef(optimisticRemovedIds);
  optimisticRemovedIdsReference.current = optimisticRemovedIds;

  const markOptimisticallyRemoved = useCallback((ids: string[]) => {
    setOptimisticRemovedIds(previous => [...new Set([...previous, ...ids])]);
  }, []);

  const shouldTriggerSearchImmediatelyReference = useRef(false);
  const previousFilterModelStringReference = useRef<string>('');

  const triggerApply = () => {
    shouldTriggerSearchImmediatelyReference.current = true;
  };

  const { applyNow, scheduleApply } = useDebouncedFilterApply(model => {
    const { filters } = serializeSearchFilterModel(model);
    setSelectedFilters(filters);
  });

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);

    if (shouldTriggerSearchImmediatelyReference.current) {
      shouldTriggerSearchImmediatelyReference.current = false;
      applyNow(newFilterModel);
    } else {
      scheduleApply(newFilterModel);
    }
  };

  const searchIdentity = { assetId, searchType, selectedFilters, searchQuery, activeThreshold };
  const columnContextKey = buildSearchContextKey(searchIdentity);
  const contextKey = buildSearchContextKey({
    ...searchIdentity,
    tenantId: selectedSearchTab?.tenantId ?? getTenantId(),
  });
  const currentContextKeyReference = useRef(contextKey);
  currentContextKeyReference.current = contextKey;

  const navigationType = useNavigationType();
  useScrollPositionRestore({
    apiReference: gridApi,
    folderKey: `search:${assetId || ''}-${searchType || ''}-${searchQuery || ''}`,
    shouldRestore: navigationType === 'POP',
  });

  const metadataFieldNames = Object.keys(filterDataMetadata ?? {});

  const { metadataColumns, columnVisibilityModel, onColumnVisibilityModelChange, isMetadataLoading } =
    useColumnVisibility({
      metadataFieldNames,
      isLoadingFieldNames: false,
      canEditMetadata: permissions.canModifyAssetMetadata,
      contextKey: columnContextKey,
      filterable: true,
      filterDataMetadata,
      onApplyFilter: triggerApply,
      hasLabels: filterDataLabels.length > 0 || loadedMatchHasLabel,
      // cross-tenant search: type the columns from the tenant being searched
      // (the active tab), not the page's home tenant
      metadataFieldsTenantId: selectedSearchTab?.tenantId ?? getTenantId(),
    });

  useEffect(() => {
    const availableColumns = new Set(['extension', 'folder', 'label', 'createdAt', 'creator']);
    for (const col of metadataColumns) {
      availableColumns.add(col.field);
    }

    const newFilterModel = selectedFiltersToGridFilterModel(selectedFilters, availableColumns);
    const newModelString = JSON.stringify(newFilterModel);
    if (previousFilterModelStringReference.current !== newModelString) {
      setFilterModel(newFilterModel);
      previousFilterModelStringReference.current = newModelString;
    }
  }, [selectedFilters, metadataColumns.length]);

  const transformedFetchMatches = async (parameters: { page: number; perPage: number }) => {
    const fetchContextKey = currentContextKeyReference.current;
    const response = await fetchMatches(parameters);
    const isStaleResponse = currentContextKeyReference.current !== fetchContextKey;
    const removedCount = optimisticRemovedIdsReference.current.length;
    setTotalResultCount(Math.max(0, response.pageData.total - removedCount));
    setTotalMatchCount(
      response.pageData.totalExact === undefined ? undefined : Math.max(0, response.pageData.totalExact - removedCount)
    );
    setTotalIsLowerBound(response.pageData.totalIsLowerBound === true);

    if (!isStaleResponse) {
      setIsDegradedSearch(previous =>
        parameters.page === 1 ? Boolean(response.degraded) : previous || Boolean(response.degraded)
      );
    }

    if (parameters.page === 1) {
      if (response.filterData?.metadata) {
        setFilterDataMetadata(response.filterData.metadata);
      }
      if (response.filterData?.extensions) {
        setFilterDataExtensions(response.filterData.extensions);
      }
      if (response.filterData?.labels) {
        setFilterDataLabels(response.filterData.labels);
      }
      if (response.filterData?.folders) {
        setFilterDataFolders(response.filterData.folders);
      }
    }

    const matchAssets: MatchAsset[] = (response.matches ?? []).map(match => {
      const matchAsset: MatchAsset = {
        ...match.asset,
        id: match.asset.id,
        contentType: 'asset',
      };

      if ('matchPercentage' in match) {
        matchAsset.matchPercentage = match.matchPercentage;
      }

      if ('forwardMatchPercentage' in match && 'reverseMatchPercentage' in match) {
        matchAsset.forwardMatchPercentage = match.forwardMatchPercentage;
        matchAsset.reverseMatchPercentage = match.reverseMatchPercentage;
      }

      return matchAsset;
    });

    if (!isStaleResponse && matchAssets.some(asset => asset.label)) {
      setLoadedMatchHasLabel(true);
    }

    return {
      matches: matchAssets,
      pageData: response.pageData,
    };
  };

  const { selectedRows, isLoadingAll, handleSelectionChange, clearSelection } = useBulkSelection<
    MatchAsset,
    { matches: MatchAsset[] }
  >({
    fetchContents: transformedFetchMatches,
    dataKey: 'matches',
    gridApi,
    selectAllAcrossPages: !assetId,
  });

  const [totalResultCount, setTotalResultCount] = useState<number | undefined>();
  // The search caps what it will count. Without this the footer shows the cap as if it were the answer.
  const [totalIsLowerBound, setTotalIsLowerBound] = useState(false);
  // Every match, which is what the footer reports. totalResultCount stays capped because it also
  // drives how many pages the grid offers, and the backend will not serve past the cap.
  const [totalMatchCount, setTotalMatchCount] = useState<number | undefined>();

  const canUseBulkActions = [permissions.canDeleteAssets].some(Boolean);

  const isCardMode = viewMode === 'card';

  const cardGridResetCounter = useRef(0);
  const previousFetchMatchesReference = useRef(fetchMatches);
  if (previousFetchMatchesReference.current !== fetchMatches) {
    previousFetchMatchesReference.current = fetchMatches;
    cardGridResetCounter.current += 1;
  }

  const transformedFetchMatchesReference = useRef(transformedFetchMatches);
  transformedFetchMatchesReference.current = transformedFetchMatches;

  const fetchCardPage = useCallback(
    async (parameters: { page: number; perPage: number }) => {
      if (!isCardMode) {
        return { items: [] as MatchAsset[], total: 0 };
      }
      const result = await transformedFetchMatchesReference.current(parameters);
      return { items: result.matches, total: result.pageData.total };
    },
    [isCardMode]
  );

  // Each tenant tab is its own result set, so it saves its own scroll offset and depth.
  const cardScrollKey = `search:${selectedSearchTab?.tenantId ?? getTenantId()}-${assetId || ''}-${searchType || ''}-${searchQuery || ''}`;
  const cardGridResetKey = `${viewMode}:${contextKey}`;
  // A mutation refreshes the data under the user rather than changing what they are looking at, so
  // it reloads the pages already scrolled through instead of dropping them back to the first page.
  const cardGridRefreshKey = String(cardGridResetCounter.current);
  const cardGridData = useCardGridData<MatchAsset>({
    fetchPage: fetchCardPage,
    resetKey: cardGridResetKey,
    refreshKey: cardGridRefreshKey,
    initialPageCount: navigationType === 'POP' ? getSavedCardPageCount(cardScrollKey) : 0,
    optimisticRemovedIds,
  });

  const cardGridItemsReference = useRef(cardGridData.items);
  cardGridItemsReference.current = cardGridData.items;

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<MatchAsset>({
    canUseContextMenu: true,
    getRowById: (rowId: string | number) => {
      const gridRow = gridApi.current?.getRow<MatchAsset>(rowId);
      if (gridRow) return gridRow;
      return cardGridItemsReference.current.find(item => item.id === String(rowId));
    },
  });
  const { downloadAsset } = useDownloadAsset();

  const handleDownloadClick = async () => {
    if (!contextMenuClickedItem) return;
    handleCloseContextMenu();
    await downloadAsset(contextMenuClickedItem);
  };

  const handleDeleteClick = () => {
    if (!contextMenuClickedItem) return;
    const clickedItemIsSelected = selectedRows.some(row => row.id === contextMenuClickedItem.id);
    if (selectedRows.length > 1 && clickedItemIsSelected) {
      setAssetsToBulkDelete(selectedRows);
    } else {
      setAssetToDelete(contextMenuClickedItem);
    }
    handleCloseContextMenu();
  };

  const extensionOptions = useMemo(
    () => filterDataExtensions.map(extension => ({ value: extension.filter, count: extension.count })),
    [filterDataExtensions]
  );
  const labelOptions = useMemo(
    () => filterDataLabels.map(label => ({ name: label.filter, depth: 0, count: label.count })),
    [filterDataLabels]
  );
  const folderOptions = useMemo(
    () => filterDataFolders.map(folder => ({ value: folder.filter, count: folder.count })),
    [filterDataFolders]
  );

  const isCrossTenant = selectedSearchTab?.tenantId !== getTenantId();

  const handleSetAsSource = useCallback(
    (newAssetId: string) => {
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);
        next.set(PARAM_NAMES.ASSET_ID, newAssetId);
        next.delete(PARAM_NAMES.EXTENSIONS);
        next.delete(PARAM_NAMES.LABELS);
        next.delete(PARAM_NAMES.FOLDERS);
        next.delete(PARAM_NAMES.METADATA);
        return next;
      });
    },
    [setSearchParameters]
  );

  const sourceRow: MatchAsset | undefined = useMemo(
    () => (searchAsset ? { ...searchAsset, contentType: 'asset' as const } : undefined),
    [searchAsset]
  );

  const pinnedRows = useMemo(
    () => ({ top: sourceRow && totalResultCount && totalResultCount > 0 ? [sourceRow] : [] }),
    [sourceRow, totalResultCount]
  );

  const showSetAsSourceColumn = Boolean(assetId) && !isCrossTenant;
  const pinnedColumns = useMemo(
    () => ({
      left: showSetAsSourceColumn ? [SET_AS_SOURCE_FIELD, 'name'] : ['name'],
      right: ['__check__'],
    }),
    [showSetAsSourceColumn]
  );

  const columns = useMemo(() => {
    const baseColumns = getColumns({
      searchType,
      canOnlySearch: !currentUserIsAtleastTenantViewer(selectedSearchTab?.tenantId),
      isCrossTenant,
      showFailureDiagnostics,
      searchAsset,
      extensionOptions,
      folderOptions,
      labelOptions,
      onApplyFilter: triggerApply,
      showLabel: isTaxonomyEnabled || filterDataLabels.length > 0,
      sourceAssetId: assetId || undefined,
      onSetAsSource: assetId && !isCrossTenant ? handleSetAsSource : undefined,
    });
    return insertMetadataColumnsAfterDetails(baseColumns, metadataColumns);
  }, [
    searchType,
    searchAsset,
    metadataColumns,
    selectedSearchTab,
    currentUserIsAtleastTenantViewer,
    isCrossTenant,
    extensionOptions,
    folderOptions,
    labelOptions,
    triggerApply,
    isTaxonomyEnabled,
    assetId,
    handleSetAsSource,
  ]);

  useEffect(() => {
    setLoadedMatchHasLabel(false);
    setFilterDataLabels([]);
    setOptimisticRemovedIds([]);
    setIsDegradedSearch(false);
  }, [contextKey]);

  const previousFiltersReference = useRef<string | undefined>(undefined);
  const previousSearchQueryReference = useRef<string | undefined>(undefined);
  const previousBulkSelectionKeyReference = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentFiltersKey = JSON.stringify(selectedFilters);
    const filtersChanged = previousFiltersReference.current !== currentFiltersKey;
    const searchQueryChanged = previousSearchQueryReference.current !== searchQuery;
    const bulkSelectionKeyChanged = previousBulkSelectionKeyReference.current !== bulkSelectionKey;

    if (filtersChanged || searchQueryChanged || bulkSelectionKeyChanged) {
      clearSelection();
      previousFiltersReference.current = currentFiltersKey;
      previousSearchQueryReference.current = searchQuery;
      previousBulkSelectionKeyReference.current = bulkSelectionKey;
    }
  }, [selectedFilters, searchQuery, bulkSelectionKey]);

  useEffect(() => {
    onSelectionCountChange?.(selectedRows.length);
  }, [selectedRows.length, onSelectionCountChange]);

  const fetchAllResults = async (): Promise<MatchAsset[]> => {
    if (!totalResultCount) {
      return [];
    }

    const allMatches: MatchAsset[] = [];
    const totalPages = Math.ceil(totalResultCount / DATA_GRID_LAZY_PAGE_SIZE);

    for (let page = 1; page <= totalPages; page += 1) {
      const response = await transformedFetchMatches({
        page,
        perPage: DATA_GRID_LAZY_PAGE_SIZE,
      });
      allMatches.push(...response.matches);
    }

    return allMatches;
  };

  return (
    <Box className={c.tableContainer}>
      {isDegradedSearch && (
        <Alert
          severity='warning'
          sx={{ mb: 2, flexShrink: 0 }}
          data-testid={SearchPageObjectModel.DegradedSearchNotice}
        >
          {searchType === 'text'
            ? "Filter options couldn't be loaded for this search. The results are complete."
            : 'This search took longer than expected and results may be incomplete. Try again, or narrow your search.'}
        </Alert>
      )}
      <Box
        className={c.dataGridWrapper}
        data-testid={SearchPageObjectModel.DataGridWrapper}
        onContextMenu={handleGridContextMenu}
        sx={isCardMode ? { display: 'grid', gridTemplate: '1fr / 1fr' } : undefined}
      >
        <BulkSelectionProvider
          value={{
            selectedRows,
            isLoadingAll,
            clearSelection,
            selectionCount: selectedRows.length,
            onItemsDeleted: markOptimisticallyRemoved,
          }}
        >
          <DataGridLazy<MatchAsset, { matches: MatchAsset[] }>
            apiRef={gridApi}
            className={c.DataGrid}
            columns={columns}
            sx={{
              '& .MuiBadge-root .MuiBadge-badge': { display: 'none' },
              ...(isCardMode && {
                gridArea: '1 / 1',
                height: 'auto !important',
                maxHeight: 'none !important',
                '& .MuiDataGrid-mainContent > :not(:first-of-type):not(.MuiDataGrid-main)': { display: 'none' },
                '& .MuiDataGrid-main > :not([data-id="gridPanelAnchor"])': { display: 'none' },
              }),
            }}
            pinnedColumns={pinnedColumns}
            pinnedRows={pinnedRows}
            // Any isRowSelectable makes the header checkbox select only loaded rows, so set it only when needed
            isRowSelectable={sourceRow ? parameters => parameters.row.id !== sourceRow.id : undefined}
            filterModel={filterModel}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            onFilterModelChange={handleFilterModelChange}
            fetchRows={isCardMode ? undefined : transformedFetchMatches}
            optimisticRemovedIds={optimisticRemovedIds}
            dataKey='matches'
            rowHeight={80}
            columnHeaderHeight={40}
            loading={isLoading || isMetadataLoading || isAnyDeletePending}
            checkboxSelection={canUseBulkActions}
            onRowSelectionModelChange={handleSelectionChange}
            error={error}
            density='compact'
            disableRowSelectionOnClick
            showToolbar
            slots={{
              toolbar: SearchTabsToolbar,
              footer: createSearchTableFooter(
                totalMatchCount ?? totalResultCount,
                totalMatchCount === undefined && totalIsLowerBound
              ),
              noRowsOverlay: NoSearchResults,
              filterPanel: SearchFilterPanel,
            }}
            slotProps={{
              toolbar: {
                shouldShowSearchTabs,
                resultCounts: searchTabCounts,
                loadingStates: searchTabLoadingStates,
                toolbarActions: (
                  <>
                    <SearchResultsDownload
                      fetchAllResults={fetchAllResults}
                      disabled={!totalResultCount}
                      searchAssetPath={searchAsset?.path}
                      searchAssetId={searchAsset?.id}
                      searchQuery={searchQuery ?? undefined}
                      searchType={searchType as SearchType | undefined}
                      searchTenantId={selectedSearchTab?.tenantId ?? getTenantId()}
                      assetTenantId={getTenantId()}
                      selectedFilters={selectedFilters}
                      minThreshold={searchType === 'visual' ? undefined : minThreshold}
                      sizeThreshold={searchType === 'visual' ? sizeThreshold : undefined}
                    />
                    {viewModeToggle}
                  </>
                ),
              } as {
                shouldShowSearchTabs?: boolean;
                toolbarActions?: React.ReactNode;
                resultCounts?: SearchTabCounts;
                loadingStates?: Record<string, boolean>;
              },
              filterPanel: {
                logicOperators: [GridLogicOperator.And],
                getColumnForNewFilter: ({ currentFilters, columns }) => {
                  const fieldsWithIsAnyOf = new Set(
                    currentFilters?.filter(f => f.operator === 'isAnyOf').map(f => f.field) ?? []
                  );

                  const availableColumn = columns.find(col => {
                    if (!col.filterable) return false;
                    if (fieldsWithIsAnyOf.has(col.field)) return false;
                    return true;
                  });

                  // eslint-disable-next-line unicorn/no-null
                  return availableColumn?.field ?? null;
                },
                filterFormProps: {
                  columnInputProps: {
                    sx: { minWidth: 150 },
                  },
                  filterColumns: ({ field, columns, currentFilters }) => {
                    const fieldsWithIsAnyOf = new Set(
                      currentFilters?.filter(f => f.operator === 'isAnyOf').map(f => f.field) ?? []
                    );

                    return columns
                      .filter(col => {
                        if (col.field === field) return true;
                        if (fieldsWithIsAnyOf.has(col.field)) return false;
                        return true;
                      })
                      .map(col => col.field);
                  },
                },
              },
            }}
            hideFooter={totalResultCount === 0}
          />
          {isCardMode && (
            <Box
              sx={{
                gridArea: '1 / 1',
                zIndex: 1,
                overflow: 'hidden',
                pointerEvents: 'none',
                paddingTop: '42px',
                '& > *': { pointerEvents: 'auto' },
              }}
            >
              <CardGrid<MatchAsset>
                items={cardGridData.items}
                total={cardGridData.total}
                renderCard={item => (
                  <SearchResultCard asset={item} onContextMenu={handleGridContextMenu} isCrossTenant={isCrossTenant} />
                )}
                getItemKey={item => item.id}
                hasNextPage={cardGridData.hasNextPage}
                isFetchingNextPage={cardGridData.isFetchingNextPage}
                fetchNextPage={cardGridData.fetchNextPage}
                isLoading={cardGridData.isLoading || isLoading}
                emptyState={<NoSearchResults />}
                scrollKey={cardScrollKey}
                pageCount={cardGridData.pageCount}
                resetKey={cardGridResetKey}
              />
            </Box>
          )}
        </BulkSelectionProvider>
      </Box>
      {(contextMenuClickedItem || getCopyValueFromColumn(columns, contextMenuClickedItem, contextMenuClickedField)) && (
        <BaseContextMenu anchorPosition={contextMenuPosition} onClose={handleCloseContextMenu}>
          {contextMenuClickedItem && contextMenuClickedField === 'name' && (
            <OpenInNewTabMenuItem item={contextMenuClickedItem} onClose={handleCloseContextMenu} />
          )}
          {contextMenuClickedItem && contextMenuClickedField === 'folder' && contextMenuClickedItem.folderId && (
            <OpenInNewTabMenuItem
              item={{ id: contextMenuClickedItem.folderId, contentType: 'folder' } as FolderContent}
              onClose={handleCloseContextMenu}
            />
          )}
          <CopyMenuItem
            columns={columns}
            row={contextMenuClickedItem}
            field={contextMenuClickedField}
            onCopy={handleCloseContextMenu}
          />
          {contextMenuClickedItem && (
            <StyledListItem onClick={() => void handleDownloadClick()}>
              <ListItemIcon>
                <DownloadIcon sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <ListItemText>Download</ListItemText>
            </StyledListItem>
          )}
          {contextMenuClickedItem && permissions.canDeleteAssets && contextMenuClickedItem.id !== sourceRow?.id && (
            <StyledListItem onClick={handleDeleteClick}>
              <ListItemIcon>
                <DeleteIcon sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </StyledListItem>
          )}
        </BaseContextMenu>
      )}
      {assetToDelete && (
        <DeleteAssetModal
          isOpen={!!assetToDelete}
          onClose={() => setAssetToDelete(undefined)}
          onDelete={() => {
            markOptimisticallyRemoved([assetToDelete.id]);
            setAssetToDelete(undefined);
          }}
          asset={assetToDelete}
        />
      )}
      {assetsToBulkDelete && (
        <BulkDeleteContentModal
          open={!!assetsToBulkDelete}
          onClose={() => setAssetsToBulkDelete(undefined)}
          selectedContent={assetsToBulkDelete}
          onDeleteComplete={() => {
            markOptimisticallyRemoved(assetsToBulkDelete.map(asset => asset.id));
            clearSelection();
            setAssetsToBulkDelete(undefined);
          }}
        />
      )}
    </Box>
  );
};
